import * as THREE from 'three'
import type { SceneObject } from '../types/scene'
import { registry } from '../components-lib/registry'
import { resolveUniformScaledParams } from './uniformScaleParams'
import { getClickWorldPosition } from '../three/DragHandler'

/** 折线网格包围盒不能当可靠顶面，不参与表面吸附 */
export const SURFACE_SNAP_EXCLUDED_TYPES = new Set([
  'infra_road_polyline',
  'infra_wall_polyline',
  'infra_river',
  'pipe_polyline',
])

/**
 * 「铺地件」：需要把底面贴在楼面/地面的组件，而不是把整体顶面吸到别的物体上。
 * 放置时用 floorSurfaceYFromHits 找脚下真正的「地面顶面」，再抬 ε，避免与厂房地面共面闪烁。
 */
export const FLOOR_LIKE_TYPES = new Set([
  'infra_indoor_floor',
  'infra_floor_zone',
  'infra_zone',
  'infra_indoor_road',
])

/** 厂房提供的可踩地面网格名（addGroundPlane / 承台 / 勒脚）；场景地面 __ground__ 除外 */
const FLOOR_CLASS_MESH_NAMES = new Set(['ground', 'base', 'plinth'])

/** 铺地件相对其踩到的厂房地面再抬高的间隙（米），彻底避开共面闪烁 */
const FLOOR_LIKE_CLEARANCE = 0.005

export function isFloorLikeType(type: string): boolean {
  return FLOOR_LIKE_TYPES.has(type)
}

/**
 * 从射线所有命中里找「可踩地面」的最高 Y：
 * - 建筑的 ground / base / plinth：算可踩地面，抬 ε 让铺地件底面落在其上方；
 * - 另一个铺地件（层叠分区/通道）：取其顶面，继续抬 ε 叠上去；
 * - 场景地面 __ground__ / 其它物体：不算，不要抬高，户外保持贴地。
 *
 * 返回 null 表示没有可踩的建筑地面，调用方用 floorY 兜底。
 */
export function floorSurfaceYFromHits(
  hits: SurfaceHit[],
  objects: SceneObject[],
): number | null {
  const byId = new Map(objects.map((o) => [o.id, o]))
  let best = -Infinity
  for (const hit of hits) {
    // 命中点如果落在可踩地面的网格上，直接取该平面（底面朝下不参与，见 coplanarScan 同款过滤）
    const meshName = hit.object.name || ''
    if (FLOOR_CLASS_MESH_NAMES.has(meshName) && hit.point) {
      best = Math.max(best, hit.point.y)
      continue
    }
    // 命中另一个铺地件（其名字不是 ground 等，但对象类型是铺地件）：取该对象顶面
    let node: THREE.Object3D | null = hit.object
    while (node) {
      const id = node.userData?.objectId as string | undefined
      if (id) {
        const obj = byId.get(id)
        if (obj && isFloorLikeType(obj.type)) {
          const topY = getObjectTopY(obj)
          if (topY !== null) best = Math.max(best, topY)
          break
        }
      }
      node = node.parent
    }
  }
  return Number.isFinite(best) ? best : null
}

function floorKeyExclude(excludeIds?: Iterable<string>): Set<string> {
  return excludeIds instanceof Set ? excludeIds : new Set(excludeIds ?? [])
}

export function wantsSurfaceSnap(e: { ctrlKey?: boolean; metaKey?: boolean }): boolean {
  return !!(e.ctrlKey || e.metaKey)
}

/**
 * 只按「实际可见」的网格算世界包围盒顶面——不能直接用 `Box3.setFromObject`，
 * 它的 `expandByObject` 不检查 `.visible`，会把隐藏的网格也算进去
 * （核实过 three.js 源码：`expandByObject` 全程没有任何可见性判断）。
 *
 * 这对厂房类组件是硬需求：拆壳视图（显示外墙/屋顶开关）把屋顶/外墙的
 * mesh.visible 置为 false 来隐藏，但它们仍在场景图里。如果直接吃全量
 * 包围盒，Ctrl 拖拽表面吸附会算出隐藏屋顶的高度，把设备吸到看不见的
 * 屋顶那么高，而不是玩家实际看到、想放的室内地坪——拆壳是为了在里面
 * 摆设备，摆的位置却对不上看到的画面就完全没意义了。
 *
 * `traverseVisible` 是 three.js 内置方法：一旦某节点 `visible===false`
 * 就直接跳过整个子树、不再往下递归，和渲染器的真实表现完全一致。
 */
function visibleTopY(root: THREE.Object3D): number | null {
  root.updateMatrixWorld(true)
  const box = new THREE.Box3()
  const meshBox = new THREE.Box3()
  let found = false
  root.traverseVisible((child) => {
    if (!(child instanceof THREE.Mesh) || !child.geometry) return
    if (!child.geometry.boundingBox) child.geometry.computeBoundingBox()
    meshBox.copy(child.geometry.boundingBox!).applyMatrix4(child.matrixWorld)
    box.union(meshBox)
    found = true
  })
  return found ? box.max.y : null
}

/**
 * 物体顶面世界 Y。优先用真实网格包围盒（只算可见部分）；无网格/全隐藏时
 * 用 position.y + getBounds().h。
 */
export function getObjectTopY(
  obj: SceneObject,
  threeObject?: THREE.Object3D | null,
): number {
  if (threeObject) {
    const topY = visibleTopY(threeObject)
    if (topY !== null) return topY
  }
  const def = registry.get(obj.type)
  if (!def) return obj.position[1]
  const bounds = def.getBounds(resolveUniformScaledParams(def, obj.params ?? {}))
  return obj.position[1] + (bounds.h ?? 0)
}

export type SurfaceHit = {
  object: THREE.Object3D
  /** 可选命中点（raycaster 返回的交点带 point；手动构造的路由可不带） */
  point?: THREE.Vector3
}

/**
 * 从射线命中推断放置高度：命中其它对象则用其顶面，否则用楼面。
 */
export function surfaceYFromHits(
  hits: SurfaceHit[],
  objects: SceneObject[],
  floorY: number,
  options?: {
    excludeIds?: Iterable<string>
    getThreeObject?: (id: string) => THREE.Object3D | undefined
  },
): number {
  const exclude = floorKeyExclude(options?.excludeIds)
  const byId = new Map(objects.map((o) => [o.id, o]))

  for (const hit of hits) {
    let node: THREE.Object3D | null = hit.object
    while (node) {
      if (node.userData?.interactable === false) break
      const name = node.name || node.parent?.name
      if (name === '__ground__') break
      const id = node.userData?.objectId as string | undefined
      if (id && !exclude.has(id)) {
        const obj = byId.get(id)
        if (!obj) break
        if (SURFACE_SNAP_EXCLUDED_TYPES.has(obj.type)) break
        return getObjectTopY(obj, options?.getThreeObject?.(id) ?? null)
      }
      node = node.parent
    }
  }
  return floorY
}

export function pickSurfaceYFromPointer(
  event: { clientX: number; clientY: number },
  canvas: HTMLCanvasElement,
  camera: THREE.Camera,
  scene: THREE.Scene,
  objects: SceneObject[],
  floorY: number,
  options?: {
    excludeIds?: Iterable<string>
    getThreeObject?: (id: string) => THREE.Object3D | undefined
  },
): number {
  const rect = canvas.getBoundingClientRect()
  const ndc = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  )
  const raycaster = new THREE.Raycaster()
  raycaster.setFromCamera(ndc, camera)
  const hits = raycaster.intersectObjects(scene.children, true)
  return surfaceYFromHits(hits, objects, floorY, options)
}

/** 托盘拖放 / Ghost：XZ 落在楼面网格，Y 吸附指针下对象顶面（没有则贴楼面） */
export function resolvePlacement(
  event: { clientX: number; clientY: number },
  canvas: HTMLCanvasElement,
  camera: THREE.Camera,
  opts: {
    floorY: number
    gridSize?: number
    scene: THREE.Scene
    objects: SceneObject[]
    excludeIds?: Iterable<string>
    getThreeObject?: (id: string) => THREE.Object3D | undefined
    snapToSurfaces?: boolean
    /** 正在放置的组件类型：铺地件走 floorSurfaceYFromHits，避免与厂房地面共面 */
    type?: string
  },
): { x: number; y: number; z: number } | null {
  const xz = getClickWorldPosition(event, canvas, camera, {
    gridSize: opts.gridSize ?? 1,
    groundY: opts.floorY,
  })
  if (!xz) return null

  let y: number
  if (isFloorLikeType(opts.type ?? '')) {
    // 铺地件：找脚下可踩的建筑地面顶面（ground/base/plinth 或另一铺地件），抬 ε 避免共面；
    // 命中其它（如屋顶/墙/室外空地）则保持贴楼面。始终不吸到屋顶。
    const rect = canvas.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    )
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(ndc, camera)
    const hits = raycaster.intersectObjects(opts.scene.children, true)
    const floorTop = floorSurfaceYFromHits(hits, opts.objects)
    y = floorTop !== null ? floorTop + FLOOR_LIKE_CLEARANCE : opts.floorY
  } else {
    y =
      opts.snapToSurfaces === false
        ? opts.floorY
        : pickSurfaceYFromPointer(event, canvas, camera, opts.scene, opts.objects, opts.floorY, {
            excludeIds: opts.excludeIds,
            getThreeObject: opts.getThreeObject,
          })
  }
  return { x: xz.x, y, z: xz.z }
}
